import React, { forwardRef, ForwardRefRenderFunction } from "react";
//#region types
import { FormHandles, TableListFieldProps } from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { SettingsFormTypes } from "models/formTypes";
import { IntegrationSettingsFormProps } from "models/propTypes/settingsPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { AppCategory } from "models/enums/apps";
import { FormEnums, StyleEnums } from "models/enums";
//#endregion
//#region components
import { GenericForm } from "components/common";
import { Chip, Grid } from "@mui/material";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const IntegrationSettingsForm: ForwardRefRenderFunction<FormHandles, IntegrationSettingsFormProps> = (props, ref) => {
  const getStatusLabel = (value: SettingsFormTypes.IntegrationItem) => {
    if (value.statusLabel) return value.statusLabel;

    return value.isActive
      ? TranslationKeys.Common.Active
      : value.activationState === WintSuperkollAPIModel.AppActivationState.Waiting
        ? TranslationKeys.Settings.WaitingForActivation
        : TranslationKeys.Common.InActive;
  };

  const getStatusClass = (value: SettingsFormTypes.IntegrationItem) => {
    switch (value.statusDisplayType) {
      case StyleEnums.DisplayType.Success:
        return cssExports.greenTag;
      case StyleEnums.DisplayType.Warning:
        return cssExports.yellowTag;
      case StyleEnums.DisplayType.Danger:
        return cssExports.redTag;
    }

    return value.isActive
      ? cssExports.greenTag
      : value.activationState === WintSuperkollAPIModel.AppActivationState.Waiting
        ? cssExports.yellowTag
        : cssExports.redTag;
  };

  return (
    <GenericForm<SettingsFormTypes.IntegrationSettings>
      formRef={ref}
      inline={false}
      {...props}
      fields={{
        apps: {
          label: "",
          type: FormEnums.FormFieldType.TABLELIST,
          extraProps: {
            resultTableProperties: {
              name: {
                description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.System),
                formatter: (value: SettingsFormTypes.IntegrationItem) =>
                  value.imageSource ? (
                    <img src={value.imageSource} height={30}></img>
                  ) : (
                    <Grid item className={cssExports.tableListMainText}>
                      {value.name}
                    </Grid>
                  ),
              },
              appCategoryName: {
                description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Categories),
                formatter: (value: SettingsFormTypes.IntegrationItem) => (
                  <Grid>
                    {LocalizationHelperInstance.getFormattedMessage(`Settings.AppsCategory_${value.appCategory}`)}
                  </Grid>
                ),
              },
              description: {
                description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Description),
                formatter: (value: SettingsFormTypes.IntegrationItem) => (
                  <Grid item className={cssExports.tableListMainText}>
                    {value.appCategory === AppCategory.Default
                      ? value.description
                      : LocalizationHelperInstance.getFormattedMessage(
                          `Settings.AppsCategoryDescription_${value.appCategory}`
                        )}
                  </Grid>
                ),
                maxWidth: "400px",
              },
              isActive: {
                description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Active),
                formatter: (value: SettingsFormTypes.IntegrationItem) => (
                  <Chip
                    label={LocalizationHelperInstance.getFormattedMessage(getStatusLabel(value))}
                    className={getStatusClass(value)}
                  />
                ),
                width: "100px",
              },
            },
            moreActions: props.appsMoreActions,
            addItemForm: props.onAddOrEditApp,
            editDialogDisableSave: (value: SettingsFormTypes.IntegrationItem) => value.statusUnavailable === true,
            hideHeader: false,
            entityText: TranslationKeys.Common.Integration,
            hideMoreActionsInForm: true,
            disableSort: true,
            icon: IconDefinitionConstants.WLink,
          } as TableListFieldProps<SettingsFormTypes.IntegrationItem>,
        },
      }}
    />
  );
};

export default forwardRef(IntegrationSettingsForm);
